import React from 'react';
import { Card, CardHeader, CardContent, CardTitle } from './ui/Card';
import { Badge } from './ui/Badge';
import { ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Brain, AlertCircle, CheckCircle2, ShieldAlert, Sparkles, Calendar, Stethoscope, ArrowUpRight } from 'lucide-react';
import { SCREENING_SECTIONS, THERAPIST_EVALUATION_SECTIONS } from '../lib/screeningQuestions';
import { evaluateScreeningAnswers } from '../lib/screeningEvaluator';

export default function AssessmentAnalysis({ assessmentData, isTherapistView = false }) {
    if (!assessmentData) {
        return <div className="p-12 text-center text-slate-500 font-medium">Analysis data not available.</div>;
    }

    const { type, score, date, questions = [], answers = {}, remarks = {}, patientName } = assessmentData;
    const isScreening = type?.toLowerCase().includes('screening');
    const isTherapistEval = type === 'Therapist Clinical Evaluation';

    // If it's a screening, evaluate with the clinical engine
    const evaluation = isScreening ? evaluateScreeningAnswers(answers, remarks) : null;

    let finalScore = evaluation ? evaluation.score : score;
    let maxScore = evaluation ? evaluation.maxScore : 20;
    let percentage = evaluation ? evaluation.percentage : 0;
    let riskLevel = evaluation ? evaluation.riskLevel : 'Moderate';
    let riskDescription = evaluation ? evaluation.riskDescription : '';

    if (!isScreening) {
        if (typeof score === 'number' && score >= 0) {
            finalScore = score;
            maxScore = assessmentData.maxScore || 100;
            percentage = assessmentData.percentage || Math.round((finalScore / maxScore) * 100);
        } else {
            const values = Object.values(answers).map(Number).filter(n => !isNaN(n) && n > 0);
            finalScore = values.reduce((sum, v) => sum + v, 0);
            maxScore = questions.length > 0 ? questions.length * 4 : 20;
            percentage = Math.round((finalScore / (maxScore || 1)) * 100);
        }
    }

    percentage = Math.max(0, Math.min(100, percentage));

    const pieData = [
        { name: 'Achieved', value: percentage },
        { name: 'Gap', value: Math.max(0, 100 - percentage) },
    ];
    const COLORS = [
        riskLevel === 'Low' ? '#10b981' : riskLevel === 'Elevated' ? '#ef4444' : '#6366f1',
        '#f1f5f9'
    ];

    const SCORES = { 
        4: "Always / Independent", 3: "Often", 2: "Sometimes", 1: "Rarely / Dependent",
        'Yes': 'Yes', 'No': 'No', 'Not Sure': 'Not Sure',
        'With Help': 'With Help', 'Partially': 'Partially'
    };

    return (
        <div className="space-y-8 font-inter">
            {/* Top Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Score & Risk Card */}
                <Card className="md:col-span-1 shadow-soft border border-slate-100 bg-white text-center rounded-[2rem] overflow-hidden">
                    <CardContent className="pt-8 px-6 pb-8">
                        <div className="h-44 w-44 mx-auto relative mb-3">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie 
                                        data={pieData} 
                                        cx="50%" 
                                        cy="50%" 
                                        innerRadius={62} 
                                        outerRadius={82} 
                                        fill="#8884d8" 
                                        dataKey="value" 
                                        stroke="none"
                                        startAngle={90}
                                        endAngle={-270}
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-3xl font-black text-slate-900 font-outfit">{percentage}%</span>
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Overall</span>
                            </div>
                        </div>

                        <h3 className="text-lg font-black text-slate-900 font-outfit mb-1">
                            {isScreening ? 'Milestone Progress' : isTherapistEval ? 'Clinical Baseline' : 'Independence Score'}
                        </h3>
                        
                        <p className="text-xs text-slate-500 font-medium mb-4 px-2">
                            {isScreening ? `${finalScore} of ${maxScore} developmental points reached` : 'Calculated evaluation score'}
                        </p>

                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-wider bg-slate-50 border border-slate-200/80">
                            <span className={`h-2.5 w-2.5 rounded-full ${
                                riskLevel === 'Low' ? 'bg-emerald-500' : riskLevel === 'Elevated' ? 'bg-red-500' : 'bg-amber-500'
                            }`} />
                            <span className={riskLevel === 'Low' ? 'text-emerald-700' : riskLevel === 'Elevated' ? 'text-red-700' : 'text-amber-700'}>
                                {riskLevel} Concern
                            </span>
                        </div>
                    </CardContent>
                </Card>

                {/* Clinical Interpretation & Specialist Recommendations */}
                <div className="md:col-span-2 space-y-6">
                    {/* Clinical Summary Banner */}
                    <Card className="shadow-soft border border-slate-100 rounded-[2rem] p-6 sm:p-7 bg-white">
                        <div className="flex items-start gap-4">
                            <div className="h-12 w-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0 mt-0.5">
                                <Brain className="h-6 w-6" />
                            </div>
                            <div className="space-y-1.5 flex-1">
                                <span className="text-[10px] font-black uppercase tracking-widest text-indigo-600 font-outfit">
                                    Clinical Diagnostic Summary
                                </span>
                                <h4 className="text-base font-black text-slate-900 font-outfit">
                                    {patientName} • {type}
                                </h4>
                                <p className="text-xs sm:text-sm text-slate-600 font-medium leading-relaxed">
                                    {riskDescription || (
                                        percentage >= 80 
                                            ? "Child demonstrates strong independent acquisition across age-appropriate milestones." 
                                            : "Mild developmental delays noted in targeted domains. Periodic screening or clinical observation suggested."
                                    )}
                                </p>
                            </div>
                        </div>

                        {/* Specialist Recommendation Box */}
                        {evaluation?.recommendedSpecialists?.length > 0 && (
                            <div className="mt-5 pt-5 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div className="space-y-0.5">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                        Recommended Specialist Consultation
                                    </p>
                                    <p className="text-xs font-bold text-slate-800">
                                        {evaluation.recommendedSpecialists[0]?.specialty}
                                    </p>
                                    <p className="text-[11px] text-slate-500 font-medium">
                                        {evaluation.recommendedSpecialists[0]?.reason}
                                    </p>
                                </div>
                                <a
                                    href="/doctors"
                                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold uppercase tracking-wider shrink-0 transition-all shadow-sm"
                                >
                                    <Calendar className="h-3.5 w-3.5" /> Book Consultation
                                </a>
                            </div>
                        )}
                    </Card>

                    {/* Red Flags Alert Card (If any) */}
                    {evaluation?.redFlags?.length > 0 && (
                        <div className="p-6 rounded-[2rem] bg-amber-50/80 border border-amber-200/80 space-y-3 shadow-sm">
                            <div className="flex items-center gap-2.5 text-amber-800">
                                <ShieldAlert className="h-5 w-5" />
                                <h4 className="text-xs font-black uppercase tracking-wider font-outfit">
                                    Clinical Red Flags / Priority Concerns ({evaluation.redFlags.length})
                                </h4>
                            </div>
                            <div className="space-y-2">
                                {evaluation.redFlags.map(rf => (
                                    <div key={rf.id} className="text-xs text-amber-900 font-medium bg-white/70 p-3 rounded-xl border border-amber-100 flex items-start gap-2.5">
                                        <span className="h-2 w-2 rounded-full bg-amber-500 mt-1.5 shrink-0" />
                                        <div>
                                            <p className="font-bold text-amber-950">{rf.title}</p>
                                            <p className="text-[11px] text-amber-800 mt-0.5">{rf.recommendation}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Domain-by-Domain Breakdown Cards */}
            {evaluation?.domainBreakdown && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between px-1">
                        <div>
                            <h3 className="text-lg font-black text-slate-900 font-outfit">
                                Domain-Wise Milestone Evaluation
                            </h3>
                            <p className="text-xs text-slate-500 font-medium">
                                Individual developmental domains evaluated against standardized milestone thresholds.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {evaluation.domainBreakdown.map(domain => {
                            const isGood = domain.percentage >= 80;
                            const isLow = domain.percentage < 60;

                            return (
                                <div 
                                    key={domain.id} 
                                    className="p-5 rounded-2xl bg-white border border-slate-100 shadow-soft space-y-3 transition-all hover:border-slate-200"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <h4 className="text-sm font-bold text-slate-800">{domain.name}</h4>
                                            <p className="text-[11px] text-slate-400 font-medium line-clamp-1">{domain.description}</p>
                                        </div>
                                        <span className="text-base font-black text-slate-900 font-outfit">{domain.percentage}%</span>
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                                        <div 
                                            className={`h-full rounded-full transition-all duration-500 ${
                                                isGood ? 'bg-emerald-500' : isLow ? 'bg-red-500' : 'bg-amber-500'
                                            }`}
                                            style={{ width: `${domain.percentage}%` }}
                                        />
                                    </div>

                                    <div className="flex items-center justify-between pt-1">
                                        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                                            {domain.score} / {domain.maxScore} pts
                                        </span>
                                        <span className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full ${
                                            isGood 
                                                ? 'bg-emerald-50 text-emerald-700' 
                                                : isLow 
                                                ? 'bg-red-50 text-red-700' 
                                                : 'bg-amber-50 text-amber-700'
                                        }`}>
                                            {domain.status}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Detailed Question Answers List */}
            <Card className="shadow-soft border border-slate-100 rounded-[2rem] overflow-hidden bg-white">
                <CardHeader className="p-6 sm:p-8 pb-4 border-b border-slate-100 flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="text-lg font-black text-slate-900 font-outfit uppercase tracking-tight">
                            Complete Question Breakdown
                        </CardTitle>
                        <p className="text-xs text-slate-400 font-medium mt-0.5">
                            Recorded caregiver and clinical observations for this screening cycle
                        </p>
                    </div>
                    <Badge className="bg-slate-50 text-slate-600 border border-slate-200 text-[10px] font-bold px-3 py-1">
                        {Object.keys(answers).length} Items Recorded
                    </Badge>
                </CardHeader>

                <CardContent className="p-6 sm:p-8 pt-4 divide-y divide-slate-100 max-h-[500px] overflow-y-auto custom-scrollbar">
                    {isScreening ? (
                        SCREENING_SECTIONS.map((section) => (
                            <div key={section.id} className="py-6 first:pt-2 space-y-4">
                                <h4 className="text-xs font-black text-indigo-600 uppercase tracking-wider">
                                    {section.title}
                                </h4>
                                <div className="space-y-2.5">
                                    {section.questions.map((q) => {
                                        const answer = answers[q.id];
                                        const isText = q.type === 'textarea' || q.type === 'text';

                                        if (isText && !answer) return null;

                                        // Accurately flag negative responses
                                        const isNegative = 
                                            (answer === 'No' && q.id !== 'vs4' && !q.id.startsWith('mh') && q.id !== 'hs5') ||
                                            (answer === 'Yes' && (q.id === 'vs4' || q.id.startsWith('mh'))) ||
                                            answer === 'Significant' ||
                                            answer === 'Very difficult' ||
                                            answer === 'Not yet speaking';

                                        return (
                                            <div 
                                                key={q.id} 
                                                className={`p-4 rounded-xl border transition-all ${
                                                    isNegative 
                                                        ? 'bg-amber-50/50 border-amber-200/60' 
                                                        : 'bg-white border-slate-100'
                                                }`}
                                            >
                                                <div className="flex items-center justify-between gap-4">
                                                    <p className={`text-xs sm:text-sm font-semibold ${isNegative ? 'text-amber-950' : 'text-slate-800'}`}>
                                                        {q.label}
                                                    </p>
                                                    {!isText && (
                                                        <Badge 
                                                            variant={isNegative ? 'warning' : 'success'} 
                                                            className={`rounded-lg text-[10px] font-bold uppercase shrink-0 px-2.5 py-1 ${
                                                                isNegative ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                            }`}
                                                        >
                                                            {answer || 'N/A'}
                                                        </Badge>
                                                    )}
                                                </div>
                                                {isText && answer && (
                                                    <p className="text-xs text-slate-600 italic mt-2.5 pl-3 border-l-2 border-indigo-400 py-1 bg-slate-50/60 rounded-r-lg font-medium">
                                                        "{answer}"
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    ) : (
                        questions.map((q, idx) => (
                            <div key={idx} className="py-4 flex gap-4 items-start">
                                <div className="mt-0.5">
                                    {(answers[idx] >= 3 || answers[idx] === 'Yes') 
                                        ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> 
                                        : <AlertCircle className="h-5 w-5 text-amber-500" />}
                                </div>
                                <div className="flex-1 space-y-1">
                                    <p className="text-sm font-bold text-slate-900">{typeof q === 'object' ? q.title : q}</p>
                                    <Badge variant="outline" className="text-[10px] font-bold">
                                        {SCORES[answers[idx]] || answers[idx] || 'N/A'}
                                    </Badge>
                                    {remarks[idx] && (
                                        <p className="text-xs text-slate-600 italic bg-slate-50 p-3 rounded-xl border border-slate-100 mt-2">
                                            "{remarks[idx]}"
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </CardContent>
            </Card>

            {/* Caregiver General Notes (If logged) */}
            {(remarks.additionalNotes || remarks.generalNotes) && (
                <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-1.5">
                    <h4 className="text-xs font-black uppercase tracking-wider text-slate-500">
                        Caregiver Session Notes
                    </h4>
                    <p className="text-xs sm:text-sm text-slate-700 italic font-medium">
                        "{remarks.additionalNotes || remarks.generalNotes}"
                    </p>
                </div>
            )}
        </div>
    );
}
